import { AxiosResponse } from 'axios';
import ThrottledApi from './api/throttledApi';
import { StoreCloseReportData } from '../features/cstore/services/pdf/types';
import { CSTORE_ENDPOINTS } from '../constants/apiEndPoints';

// ===== Sales Summary Interfaces =====
export interface FuelGradeQuantity {
  grade_id: string;
  grade_name: string;
  quantity: number;
  sales_amount?: number;
}

export interface GasSalesByCategory {
  regular?: {
    quantity: number;
    sales: number;
  };
  plus?: {
    quantity: number;
    sales: number;
  };
  super?: {
    quantity: number;
    sales: number;
  };
  diesel?: {
    quantity: number;
    sales: number;
  };
}

export interface SalesSummaryData {
  total_sales: number;
  gas_sales?: number;
  merchandise_sales?: number;
  transaction_count: number;
  total_transaction_count: number;
  average_transaction: number;
  tax_collected: number;
  void_transaction_count: number;
  void_total: number;
  payout_transaction_count: number;
  payout_total: number;
  lottery_payout_from_transaction_line?: number;
  lottery_payout_from_financial_event?: number;
  refund_transaction_count: number;
  refund_total: number;
  void_line_count: number;
  void_line_total: number;
  fuel_by_grade: FuelGradeQuantity[];
  gas_sales_by_category?: GasSalesByCategory;
  sales_change: number;
  transaction_change: number;
  avg_transaction_change: number;
  tax_change: number;
}

export interface SalesSummaryResponse {
  success: boolean;
  data: SalesSummaryData | null;
  error?: string;
}

export interface SalesPeriod {
  key: string;
  status: 'closed' | 'open';
  sequence_number: number | null;
  period_from: string;
  period_to: string | null;
  transaction_count: number;
}

export interface SalesPeriodsData {
  store_id: string;
  date: string;
  business_day: SalesPeriod | null;
  periods: SalesPeriod[];
}

export interface ShiftSalesSummaryData {
  store_id: string;
  date: string;
  period: SalesPeriod;
  sales_summary: SalesSummaryData;
  lotto_sales: number;
  scratcher_sales: number;
  store_close_report: StoreCloseReportData;
}

// ===== Departments Interfaces =====
export interface ApiDepartment {
  dept_code: string;
  name: string;
}

export interface ApiDepartmentsResponse {
  store_id: string;
  departments: ApiDepartment[];
  count: number;
}

export interface DepartmentsServiceResponse {
  success: boolean;
  data: ApiDepartmentsResponse | null;
  error: any;
}

// ===== Transaction Interfaces =====
export interface ApiLineItem {
  id: number;
  line_type: string;
  item_description: string | null;
  fuel_description: string | null;
  item_pos_code?: string | null;
  upc_code?: string | null;
  status?: string | null;
  item_sales_amount: number;
  fuel_sales_amount: number;
  item_sales_quantity: number;
  fuel_sales_quantity: number;
  fuel_grade_id: string | null;
  item_merchandise_code: number | null;
  fuel_merchandise_code: number | null;
  fuel_grade_name?: string | null;
  merch_description?: string | null;
  merch_actual_sales_price?: number;
  merch_sales_quantity?: number;
  merch_sales_amount?: number;
}

export interface ApiTender {
  id: number;
  tender_code: string;
  tender_sub_code: string;
  tender_amount: number;
  auth_approval_reference_code: string | null;
  auth_reference_number: string | null;
  acct_id: string | null;
  acct_name: string | null;
}

export interface ApiTransaction {
  transaction_id: number;
  store_name: string;
  categories: string[];
  date: string;
  time: string;
  cashier_id: number | string | null;
  cashier_name?: string | null;
  register_id: string;
  till_id: string;
  total_amount: number;
  tax_amount: number;
  tax_exempt_amount: number;
  taxable_amount: number;
  event_kind: string;
  offline_flag: boolean;
  training_mode_flag: boolean;
  outside_sales_flag: boolean;
  suspend_flag: boolean;
  tender_code: string | null;
  tender_sub_code: string | null;
  account_name: string | null;
  payout_reason: string | null;
}

export interface ApiTransactionDetail {
  transaction_id: number;
  event_kind: string;
  transaction_type?: string | null;
  business_date: string;
  store_name: string;
  categories: string[];
  receipt_date: string;
  receipt_time: string;
  cashier_id: number | string | null;
  cashier_name?: string | null;
  register_id: string;
  till_id: string;
  sum_transaction_total_grand_amount: number;
  sum_transaction_total_tax_sales_amount: number;
  sum_transaction_total_tax_exempt_amount: number;
  sum_transaction_total_net_amount: number;
  offline_flag: boolean;
  training_mode_flag: boolean;
  outside_sales_flag: boolean;
  suspend_flag: boolean;
  line_items: ApiLineItem[];
  tenders: ApiTender[];
  payout_tender_code: string | null;
  payout_tender_sub_code: string | null;
  payout_account_name: string | null;
  payout_reason: string | null;
}

export interface ApiTransactionResponse {
  transactions: ApiTransaction[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface ApiTenderTypeOption {
  tender_type: string;
  count: number;
}

export interface ApiTenderTypesResponse {
  store_id: string;
  date: string;
  tender_types: ApiTenderTypeOption[];
  count: number;
}

export interface ApiScratcherPluOption {
  plu_code: string;
  game_id: number;
  game_name: string;
  transaction_count: number;
  quantity_sold: number;
  total_sales: number;
}

export interface ApiScratcherPluListResponse {
  store_id: string;
  date: string;
  plu_list: ApiScratcherPluOption[];
  count: number;
}

export interface TransactionServiceResponse {
  success: boolean;
  data: ApiTransactionResponse | null;
  error: string | null;
}

export interface TransactionDetailServiceResponse {
  success: boolean;
  data: ApiTransactionDetail | null;
  error: string | null;
}

export interface StoreCloseReportServiceResponse {
  success: boolean;
  data: StoreCloseReportData | null;
  error: string | null;
}

export interface TenderTypesServiceResponse {
  success: boolean;
  data: ApiTenderTypesResponse | null;
  error: string | null;
}

export interface ScratcherPluListServiceResponse {
  success: boolean;
  data: ApiScratcherPluListResponse | null;
  error: string | null;
}

/**
 * Fetch store transactions with filters
 */
export const getStoreTransactions = async (
  storeId: string,
  date: string,
  category: string = 'all',
  filter: string = 'all',
  limit: number,
  pluCode?: string | null,
  tenderType?: string | null
): Promise<TransactionServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      CSTORE_ENDPOINTS.GET_STORE_TRANSACTIONS(storeId, date, category, filter, limit, pluCode, tenderType),
      {}
    ) as AxiosResponse;

    if (response?.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: true,
      data: { transactions: [], pagination: { total: 0, limit, offset: 0, has_more: false } },
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch transactions'
    };
  }
};

export const getStoreTenderTypes = async (
  storeId: string,
  date: string
): Promise<TenderTypesServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      CSTORE_ENDPOINTS.GET_STORE_TENDER_TYPES(storeId, date),
      {}
    ) as AxiosResponse<ApiTenderTypesResponse>;

    if (response?.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: true,
      data: { store_id: storeId, date, tender_types: [], count: 0 },
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch tender types'
    };
  }
};

export const getStoreScratcherPluList = async (
  storeId: string,
  date: string
): Promise<ScratcherPluListServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      CSTORE_ENDPOINTS.GET_SCRATCHER_PLU_LIST(storeId, date),
      {}
    ) as AxiosResponse<ApiScratcherPluListResponse>;

    if (response?.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: true,
      data: { store_id: storeId, date, plu_list: [], count: 0 },
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch scratcher PLU list'
    };
  }
};

/**
 * Fetch transaction details
 */
export const getTransactionDetails = async (
  storeId: string,
  transactionId: string,
  date: string,
  registerId?: string
): Promise<TransactionDetailServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      CSTORE_ENDPOINTS.GET_TRANSACTION_DETAILS(storeId, transactionId, date, registerId),
      {}
    ) as AxiosResponse;

    if (response?.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No transaction details found'
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch transaction details'
    };
  }
};

/**
 * Fetch store close report
 */
export const getStoreCloseReport = async (
  storeId: string,
  fromDate: string,
  toDate: string
): Promise<StoreCloseReportServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      CSTORE_ENDPOINTS.GET_STORE_CLOSE_REPORT(storeId, fromDate, toDate),
      {}
    ) as AxiosResponse;

    if (response?.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No store close report data found'
    };
  } catch (error: any) {
    let errorMessage = 'Failed to fetch store close report';

    if (error.response?.data) {
      const errorData = error.response.data;
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail
            .map((item: any) => item?.msg || '')
            .filter(Boolean)
            .join('; ') || errorMessage;
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * Fetch sales summary
 */
export const getSalesSummary = async (
  storeId: string,
  date: string,
  category: string = 'all',
  limit: number
): Promise<SalesSummaryResponse> => {
  try {
    const response = await ThrottledApi.get(
      CSTORE_ENDPOINTS.GET_SALES_SUMMARY(storeId, date, category, limit),
      {}
    ) as AxiosResponse<SalesSummaryData>;

    if (response && response.data) {
      return { success: true, data: response.data };
    }

    return { success: false, data: null, error: 'Empty response' };
  } catch (error: any) {
    return { success: false, data: null, error: error?.message || 'Failed to fetch sales summary' };
  }
};

export const getSalesPeriods = async (
  storeId: string,
  date: string
): Promise<SalesPeriodsData> => {
  const response = await ThrottledApi.get(
    CSTORE_ENDPOINTS.GET_SALES_PERIODS(storeId, date),
    {}
  ) as AxiosResponse<SalesPeriodsData>;
  return response.data;
};

export const getShiftSalesSummary = async (
  storeId: string,
  date: string,
  shift: string
): Promise<ShiftSalesSummaryData> => {
  const response = await ThrottledApi.get(
    CSTORE_ENDPOINTS.GET_SHIFT_SALES_SUMMARY(storeId, date, shift),
    {}
  ) as AxiosResponse<ShiftSalesSummaryData>;
  return response.data;
};

/**
 * Fetch store departments/categories
 */
export const getStoreDepartments = async (
  storeId: string,
  date: string
): Promise<DepartmentsServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      CSTORE_ENDPOINTS.GET_STORE_DEPARTMENTS(storeId, date),
      {}
    ) as AxiosResponse<ApiDepartmentsResponse>;

    if (response?.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: true,
      data: { store_id: storeId, departments: [], count: 0 },
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message || 'Failed to fetch departments'
    };
  }
};

export const transactionService = {
  getStoreTransactions,
  getStoreTenderTypes,
  getStoreScratcherPluList,
  getTransactionDetails,
  getStoreCloseReport
};

export const salesService = {
  getSalesSummary
};

export const departmentsService = {
  getStoreDepartments
};

export const cstoreService = {
  ...transactionService,
  ...salesService,
  ...departmentsService
};
